import React from 'react';
import {
  AutoAwesomeMotionOutlined,
  CheckCircle,
  ContentCopyOutlined,
  MailOutline,
  PlaylistAddCheck,
} from '@mui/icons-material';
import { IconButton, Link, List, styled } from '@mui/material';
import { Alert } from 'components/Alert/Alert';
import { SettingsListSubheader } from 'components/Settings/SettingsListSubheader';
import { useDeviceType } from 'contexts/ThemeContext/hooks/useDeviceType';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { useAppStatus } from 'features/appStatus/hooks/useAppStatus';
import { useCopyEmail } from 'features/settings/hooks/useCopyEmail';
import { getContactLinkData } from 'utils';
import { safeFormatToNational } from 'utils/phoneNumberUtils/phoneNumberUtils';
import { useLoggedInUser } from 'features/auth/hooks/useAuthData';
import { ManageAccount } from '../ManageAccount/ManageAccount';
import { AccountSettingsItem } from './AccountSettingsItem';

const ContactLink = styled(Link)(() => ({
  display: 'block',
}));

export const AccountSettings = () => {
  const { isDesktop } = useDeviceType();
  const { translation } = useTranslationContext();
  const { version } = useAppStatus();
  const user = useLoggedInUser();
  const { copyAlertState, handleCopyEmail, dismissCopyAlert } = useCopyEmail();
  const email = user?.email || '';

  const contactLinkData = getContactLinkData({
    type: user?.hospital.contact_link_type,
    value: user?.hospital.contact_link,
  });

  return (
    <>
      <List>
        <SettingsListSubheader id="account-settings__header" isDesktop={isDesktop}>
          {translation('account_settings')}
        </SettingsListSubheader>
        <AccountSettingsItem
          id="account-settings__email"
          primaryText={translation('email')}
          secondaryText={email}
          icon={<MailOutline color="secondary" />}
          secondaryAction={
            <IconButton
              id="account-settings__copy-email-button"
              aria-label={translation('copy_to_clipboard')}
              onClick={() => handleCopyEmail(email)}
              size="small"
              color="secondary"
              disabled={!email}
            >
              <ContentCopyOutlined fontSize="small" />
            </IconButton>
          }
        />
        <AccountSettingsItem
          id="account-settings__app-access"
          icon={<PlaylistAddCheck color="secondary" />}
          primaryText={translation('app_access')}
          secondaryText={
            <>
              {user?.hospital?.display_name}
              {contactLinkData ? (
                <ContactLink href={contactLinkData.url} underline="none">
                  {safeFormatToNational(contactLinkData.displayValue)}
                </ContactLink>
              ) : null}
            </>
          }
        />
        <AccountSettingsItem
          id="account-settings__version"
          icon={<AutoAwesomeMotionOutlined color="secondary" />}
          primaryText={translation('version')}
          secondaryText={version}
        />
        <ManageAccount />
      </List>
      <Alert
        isPresented={copyAlertState.isPresented}
        variant="filled"
        severity={copyAlertState.severity}
        message={translation(copyAlertState.messageKey)}
        icon={copyAlertState.severity === 'success' ? <CheckCircle /> : undefined}
        handleDismiss={dismissCopyAlert}
      />
    </>
  );
};
